import { Capacitor, registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { cloudReportLink } from '../../state/reportCloudStore';

// Opens a PDF kept in the account (Owner decision list 2026-10-19 items 25 / 27): the server checks the
// file is this account's and answers a link that works for five minutes. In a browser the window is
// opened at the tap itself (a window opened after the network answer would be blocked as a pop-up); where
// pop-ups are blocked altogether the file is downloaded instead (the link answers as an attachment, so the
// page stays where it is).
//
// Android (acceptance 2026-10-21, issue 13): the link used to go to Chrome, which left the app and saved
// the client's report into the phone's public Downloads folder. The file is now downloaded into the app's
// own cache and shown in the phone's PDF viewer (android/…/FileViewerPlugin.java).
interface FileViewerPlugin {
  open(options: { path: string; mimeType?: string }): Promise<void>;
}

const FileViewer = registerPlugin<FileViewerPlugin>('FileViewer');

function cacheName(fileName: string): string {
  const cleaned = fileName.replace(/[\\/:*?"<>|]/g, '_').trim();
  return cleaned.length > 0 ? cleaned : 'report.pdf';
}

async function openOnAndroid(url: string, fileName: string): Promise<boolean> {
  // downloadFile's `recursive` does not create the folder on Android (seen on the emulator 2026-10-21:
  // ENOENT), so the folder is made first; "already exists" is fine.
  await Filesystem.mkdir({ path: 'account-reports', directory: Directory.Cache, recursive: true }).catch(() => undefined);
  const downloaded = await Filesystem.downloadFile({ url, path: `account-reports/${cacheName(fileName)}`, directory: Directory.Cache, recursive: true });
  const path = downloaded.path ?? (await Filesystem.getUri({ path: `account-reports/${cacheName(fileName)}`, directory: Directory.Cache })).uri;
  await FileViewer.open({ path, mimeType: 'application/pdf' });
  return true;
}

export async function openCloudReport(fileId: string, fileName: string): Promise<boolean> {
  const native = Capacitor.isNativePlatform();
  let pending: Window | null = null;
  if (!native) {
    try {
      pending = window.open('', '_blank');
    } catch {
      pending = null;
    }
  }
  const url = await cloudReportLink(fileId);
  if (!url) {
    pending?.close();
    return false;
  }
  if (pending) {
    pending.location.href = url;
    return true;
  }
  if (native && Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('FileViewer')) {
    try {
      return await openOnAndroid(url, fileName);
    } catch {
      return false;
    }
  }
  if (native) {
    window.open(url, '_blank');
    return true;
  }
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}
